import { createFsAdapter, type WebContainerFS } from "./fs";
import { createWatchPaths } from "./watch";
import { wrapProcess } from "./process";
import { createJshProcess } from "./jsh";
import { installPreviewMessages } from "./preview";
import type { WebContainerProcess } from "./types";

export type { WebContainerProcess } from "./types";
export type { PathWatcherEvent } from "./types";

// Mirrors @webcontainer/api's PreviewMessageType. String values MUST match the
// literals emitted by preview.ts (PREVIEW_ERROR_CATCHER) so Dualite's
// previews store can compare message.type directly.
export enum PreviewMessageType {
  PREVIEW_UNCAUGHT_EXCEPTION = "PREVIEW_UNCAUGHT_EXCEPTION",
  PREVIEW_UNHANDLED_REJECTION = "PREVIEW_UNHANDLED_REJECTION",
  PREVIEW_CONSOLE_ERROR = "PREVIEW_CONSOLE_ERROR",
}

// Mirrors @webcontainer/api's PreviewMessage. `type` is the bare enum (NOT
// widened with `| string`): a string-enum value both compares against its
// string-literal (`message.type === "PREVIEW_CONSOLE_ERROR"`) and stays
// assignable to a consumer's `PreviewMessageType`-typed field after narrowing
// — widening to `| string` breaks the latter. Fields are non-optional where
// dualite accesses them without guards (message.message, message.args.map).
export interface PreviewMessage {
  type: PreviewMessageType;
  message: string;
  stack?: string;
  args: unknown[];
}

interface BootOptions {
  workdirName?: string;
  coep?: string; // accepted + ignored (Nodepod handles isolation)
  forwardPreviewErrors?: boolean;
}

type NodepodFactory = (opts: {
  workdir: string;
  enableSharedArrayBuffer: boolean;
  watermark: boolean;
  onServerReady: (port: number, url: string) => void;
}) => Promise<any>;

type WCEvent = "port" | "server-ready" | "error" | "preview-message";

export class WebContainer {
  readonly workdir: string;
  readonly fs: WebContainerFS;
  readonly internal: { watchPaths: ReturnType<typeof createWatchPaths> };

  private _nodepod: any;
  private _handlers: Record<WCEvent, Array<(...a: any[]) => void>> = {
    port: [], "server-ready": [], error: [], "preview-message": [],
  };
  #previewCleanup?: () => void;

  private constructor(nodepod: any, workdir: string) {
    this._nodepod = nodepod;
    this.workdir = workdir;
    this.fs = createFsAdapter(nodepod);
    this.internal = { watchPaths: createWatchPaths(nodepod) };
  }

  static async boot(opts: BootOptions = {}, factory?: NodepodFactory): Promise<WebContainer> {
    const workdir = `/home/${opts.workdirName ?? "project"}`;
    const make: NodepodFactory =
      factory ??
      (async (o) => (await import("@dualite/nodecontainer")).Nodepod.boot(o));

    let wc: WebContainer;

    // Track each listening port's URL so we can report it again on close.
    // nodepod's `server-close` event carries only (pid, port), but the
    // WebContainer `port` "close" event — and the preview store that consumes
    // it — expect a URL alongside the port.
    const serverUrls = new Map<number, string>();

    const nodepod = await make({
      workdir,
      enableSharedArrayBuffer: false,
      watermark: false,
      onServerReady: (port, url) => {
        serverUrls.set(port, url);
        wc?.emit("server-ready", port, url);
        wc?.emit("port", port, "open", url);
      },
    });
    wc = new WebContainer(nodepod, workdir);

    // Bridge nodepod's server lifecycle to a WebContainer `port` "close" event.
    // nodepod emits `server-close` when a listening process dies (e.g. the dev
    // server is Ctrl-C'd on Restart), but this facade previously only ever
    // emitted "open" — so the preview store never cleared `activePreview`, the
    // preview never dropped to its loading state, and the iframe was never
    // reloaded on restart. Emitting "close" here restores parity with the real
    // @webcontainer/api, which emitted both "open" and "close".
    const processManager: any = (nodepod as any)?.processManager;
    if (processManager && typeof processManager.on === "function") {
      processManager.on("server-close", (_pid: number, port: number) => {
        const url = serverUrls.get(port) ?? "";
        serverUrls.delete(port);
        wc?.emit("port", port, "close", url);
      });
    }

    if (opts.forwardPreviewErrors) {
      wc.#previewCleanup = await installPreviewMessages(nodepod, (msg) => wc.emit("preview-message", msg));
    }
    return wc;
  }

  async mount(files: Record<string, string | Uint8Array>): Promise<void> {
    for (const [path, contents] of Object.entries(files)) {
      await this.fs.writeFile(path, contents);
    }
  }

  // Terminal size is intentionally ignored (no PTY sizing needed; resize is a no-op).
  async spawn(
    cmd: string,
    args?: string[],
    _opts?: { terminal?: { cols: number; rows: number } },
  ): Promise<WebContainerProcess> {
    if (cmd === "/bin/jsh") {
      // An interactive terminal opts into cooked-mode line editing (echo,
      // Backspace, CR-as-Enter) by passing `--interactive`. The AI/preview
      // shell spawns without it and keeps the programmatic (no-echo) path.
      const echo = args?.includes("--interactive") ?? false;
      return createJshProcess(this._nodepod, { echo });
    }
    const proc = await this._nodepod.spawn(cmd, args);
    return wrapProcess(proc);
  }

  on(event: "port", handler: (port: number, type: "open" | "close", url: string) => void): void;
  on(event: "server-ready", handler: (port: number, url: string) => void): void;
  on(event: "error", handler: (error: unknown) => void): void;
  on(event: "preview-message", handler: (message: PreviewMessage) => void): void;
  on(event: WCEvent, handler: (...args: any[]) => void): void {
    this._handlers[event].push(handler);
  }

  private emit(event: WCEvent, ...args: any[]): void {
    for (const h of this._handlers[event]) h(...args);
  }

  teardown(): void {
    this.#previewCleanup?.();
    this._nodepod.teardown();
  }
}
